import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  MapPinIcon,
  CurrencyYenIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const ChatListPage = () => {
  const { user } = useAuth();
  const [chatRooms, setChatRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChatRooms();
  }, []);

  const fetchChatRooms = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);

      const response = await authApi.get('/chat/rooms');
      setChatRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error);
      toast.error('チャットルームの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
      return `${diffMinutes}分前`;
    } else if (diffHours < 24) {
      return `${diffHours}時間前`;
    } else if (diffDays < 7) {
      return `${diffDays}日前`;
    } else {
      return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
    }
  };

  const getOtherUserName = (chatRoom) => {
    return user.id === chatRoom.employer_id
      ? chatRoom.job_seeker_name
      : chatRoom.employer_name;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900 text-center">チャット</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto">
        {chatRooms.length === 0 ? (
          <div className="text-center py-12 px-4">
            <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              まだチャットがありません
            </h3>
            <p className="text-gray-600 mb-6">
              求人に応募してマッチングが成立すると、チャットが開始されます。
            </p>
            <Link
              to="/jobs"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              求人を探す
            </Link>
          </div>
        ) : (
          <div className="bg-white">
            {chatRooms.map((chatRoom) => (
              <Link
                key={chatRoom.id}
                to={`/chat/${chatRoom.id}`}
                className="block border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Job Title and Other User */}
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {chatRoom.job_title}
                        </h3>
                        {chatRoom.unread_count > 0 && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                            {chatRoom.unread_count}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 mb-2">
                        {getOtherUserName(chatRoom)}
                      </p>

                      {/* Last Message */}
                      {chatRoom.last_message && (
                        <p className="text-sm text-gray-500 truncate mb-2">
                          {chatRoom.last_message}
                        </p>
                      )}

                      {/* Timestamp */}
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>
                          {formatLastMessageTime(chatRoom.last_message_at)}
                        </span>
                        <span>
                          {user.id === chatRoom.employer_id ? '事業者として' : '求職者として'}
                        </span>
                      </div>
                    </div>

                    <div className="ml-4">
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListPage;